// Pflichtfelder der Kunden-Stammdaten: welche fehlen, und wie viele.
//
// Die Kundenliste zeigte bis 30.07.2026 nur einen Prozentwert („78") — daraus ließ sich
// weder ablesen, wie viel Arbeit fehlt, noch welches Feld. Hier steht deshalb die Liste
// der fehlenden Felder im Mittelpunkt; der Prozentwert fällt als Nebenprodukt ab.
// REIN (kein React, keine IO) → unit-testbar.

/** Feldname → Beschriftung, wie sie auch im Stammdaten-Editor steht. */
export const PFLICHTFELD_LABELS: Record<string, string> = {
  name: 'Firmenname',
  rechtsform: 'Rechtsform',
  antragstellerTyp: 'Antragstellertyp',
  gruendung: 'Gründungsdatum',
  anrede: 'Anrede',
  vorname: 'Vorname',
  nachname: 'Nachname',
  address: 'Straße',
  hausnummer: 'Hausnummer',
  plz: 'PLZ',
  ort: 'Ort',
  land: 'Land',
  email: 'E-Mail',
  phoneVorwahl: 'Telefon-Vorwahl',
  phoneNummer: 'Telefonnummer',
  iban: 'IBAN',
  website: 'Website',
  wirtschaftszweig: 'Wirtschaftszweig',
  personentyp: 'Personentyp',
  vorsteuerabzug: 'Vorsteuerabzug',
  mitarbeiterzahl: 'Mitarbeiterzahl',
  unternehmenstyp: 'Unternehmenstyp',
  geschaeftsgegenstand: 'Geschäftsgegenstand',
  taxId: 'Steuer-ID',
  steueridentnummer: 'Steueridentnummer',
  geburtsdatum: 'Geburtsdatum',
  artJuristischePerson: 'Art der juristischen Person',
  steuernummer: 'Steuernummer',
  jahresumsatz: 'Jahresumsatz oder Bilanzsumme',
};

/** Immer verlangte Felder, unabhängig vom Personentyp. */
export const BASE_FIELDS = [
  'name', 'rechtsform', 'antragstellerTyp', 'gruendung', 'anrede', 'vorname', 'nachname',
  'address', 'hausnummer', 'plz', 'ort', 'land', 'email', 'phoneVorwahl', 'phoneNummer',
  'iban', 'website', 'wirtschaftszweig', 'personentyp', 'vorsteuerabzug', 'mitarbeiterzahl',
  'unternehmenstyp', 'geschaeftsgegenstand', 'taxId',
] as const;

const gefuellt = (v: unknown) => v !== null && v !== undefined && String(v).trim() !== '';

/**
 * Welche Pflichtfelder sind leer? Reihenfolge = Reihenfolge im Editor, damit die Liste
 * beim Nachtragen von oben nach unten abgearbeitet werden kann.
 *
 * Personentyp-abhängige Felder zählen nur, wenn der Typ feststeht — sonst würde ein Kunde
 * ohne Personentyp Felder als fehlend gemeldet bekommen, die er nie ausfüllen muss.
 * Jahresumsatz UND Bilanzsumme gelten zusammen als ein Feld (eines davon genügt).
 */
export function fehlendePflichtfelder(c: Record<string, unknown>): string[] {
  const fehlt: string[] = [];
  for (const f of BASE_FIELDS) {
    if (!gefuellt(c[f])) fehlt.push(f);
  }
  if (c.personentyp === 'Natürliche Person') {
    if (!gefuellt(c.steueridentnummer)) fehlt.push('steueridentnummer');
    if (!gefuellt(c.geburtsdatum)) fehlt.push('geburtsdatum');
  } else if (c.personentyp === 'Juristische Person') {
    if (!gefuellt(c.artJuristischePerson)) fehlt.push('artJuristischePerson');
    if (!gefuellt(c.steuernummer)) fehlt.push('steuernummer');
  }
  if (!gefuellt(c.jahresumsatz) && !gefuellt(c.jahresbilanzsumme)) fehlt.push('jahresumsatz');
  return fehlt;
}

/** Wie viele Pflichtfelder gibt es bei diesem Kunden insgesamt? */
export function anzahlPflichtfelder(c: Record<string, unknown>): number {
  const zusatz = c.personentyp === 'Natürliche Person' || c.personentyp === 'Juristische Person' ? 2 : 0;
  return BASE_FIELDS.length + zusatz + 1;
}

/** Anteil ausgefüllter Pflichtfelder in Prozent (0–100). */
export function calcCompletionPct(c: Record<string, unknown>): number {
  const total = anzahlPflichtfelder(c);
  const fehlt = fehlendePflichtfelder(c).length;
  return Math.round((total - fehlt) / total * 100);
}

/** Beschriftungen der fehlenden Felder, fertig für Tooltip und Aufklappbereich. */
export function fehlendeLabels(c: Record<string, unknown>): string[] {
  return fehlendePflichtfelder(c).map(f => PFLICHTFELD_LABELS[f] ?? f);
}
